import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const CHROME_EXTENSION_URL = 'https://chromewebstore.google.com/detail/colquest-mapper/lpkfkeiamhljcomokmdeknfpamhaidhl';

const ChromeIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L9.5 12l-1.64 4.5A7.96 7.96 0 0 0 12 20zm-1.53-5L7.8 8.5A7.96 7.96 0 0 1 12 4c2.93 0 5.5 1.57 6.9 3.92L14.57 15H10.47zM16 12c0 2.21-1.79 4-4 4s-4-1.79-4-4 1.79-4 4-4 4 1.79 4 4zm.9 3.08L15.24 12l1.66-4.58A7.98 7.98 0 0 1 20 12c0 2.35-1.02 4.46-2.64 5.92l-.46-2.84z"/>
  </svg>
);

const MapperPage: React.FC = () => {
  const { lang } = useParams();
  const { t, language, setLanguage } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'en' || lang === 'fr')) {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);

  return (
    <div className="mapper-page">
      {/* Hero */}
      <section className="mapper-hero">
        <div className="mapper-hero-content">
          <div className="mapper-hero-title-row">
            <h1>🗺️ {t('mapper.title')}</h1>
            <span className="mapper-beta">{t('mapper.beta')}</span>
          </div>
          <p className="mapper-tagline">{t('mapper.tagline')}</p>
          <a
            href={CHROME_EXTENSION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mapper-cta-button"
            data-umami-event="mapper-chrome-store-click"
          >
            <ChromeIcon /> {t('mapper.cta')}
          </a>
          <p className="mapper-cta-note">{t('mapper.ctaNote')}</p>
        </div>
      </section>

      {/* How it works */}
      <section className="mapper-how-it-works">
        <h2>{t('mapper.howItWorks.title')}</h2>
        <div className="mapper-steps">
          <article className="mapper-step">
            <div className="mapper-step-number">1</div>
            <div className="mapper-step-body">
              <h3>{t('mapper.howItWorks.step1.title')}</h3>
              <p>{t('mapper.howItWorks.step1.description')}</p>
            </div>
          </article>
          <article className="mapper-step">
            <div className="mapper-step-number">2</div>
            <div className="mapper-step-body">
              <h3>{t('mapper.howItWorks.step2.title')}</h3>
              <p>{t('mapper.howItWorks.step2.description')}</p>
            </div>
          </article>
          <article className="mapper-step">
            <div className="mapper-step-number">3</div>
            <div className="mapper-step-body">
              <h3>{t('mapper.howItWorks.step3.title')}</h3>
              <p>{t('mapper.howItWorks.step3.description')}</p>
            </div>
          </article>
        </div>
      </section>

      {/* Platforms */}
      <section className="mapper-platforms">
        <h2>{t('mapper.platforms.title')}</h2>
        <div className="mapper-platform-cards">
          <div className="mapper-platform-card">
            <img src="/images/strava_logo.png" alt="Strava" className="mapper-platform-logo" />
            <span className="mapper-platform-name">{t('mapper.platforms.strava')}</span>
          </div>
          <div className="mapper-platform-card">
            <img src="/images/komoot_logo.png" alt="Komoot" className="mapper-platform-logo" />
            <span className="mapper-platform-name">{t('mapper.platforms.komoot')}</span>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="mapper-bottom-cta">
        <h2>{t('mapper.bottomCta.title')}</h2>
        <a
          href={CHROME_EXTENSION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mapper-cta-button"
          data-umami-event="mapper-chrome-store-click-bottom"
        >
          <ChromeIcon /> {t('mapper.cta')}
        </a>
      </section>
    </div>
  );
};

export default MapperPage;
